import { forwardRef, useCallback, useEffect, useLayoutEffect, useRef, useState, type InputHTMLAttributes } from 'react';
import { CalendarDays, ChevronLeft, ChevronRight } from 'lucide-react';

/**
 * Date field (04/11/2026): the native <input type="date"> stays (typing, forms, react-hook-form register), but
 * the browser picker, in the browser language and starting on Sunday, is replaced by Deskter's calendar in the
 * page language, weeks from Monday in Italian. A chosen day is written through the native value setter plus an
 * input event, so controlled inputs and register() both see it.
 */
const LABELS = {
  it: { open: 'Apri il calendario', prev: 'Mese precedente', next: 'Mese successivo', today: 'Oggi', clear: 'Cancella' },
  en: { open: 'Open the calendar', prev: 'Previous month', next: 'Next month', today: 'Today', clear: 'Clear' },
};

const pad = (n: number) => String(n).padStart(2, '0');
const iso = (y: number, m: number, d: number) => `${y}-${pad(m + 1)}-${pad(d)}`;
const todayIso = () => {
  const d = new Date();
  return iso(d.getFullYear(), d.getMonth(), d.getDate());
};

function pageLang(): 'it' | 'en' {
  return typeof document !== 'undefined' && document.documentElement.lang.startsWith('en') ? 'en' : 'it';
}

/** First day of the week of the page language: 1 Monday … 7 Sunday. */
function firstDay(lang: string): number {
  try {
    const loc = new Intl.Locale(lang === 'en' ? 'en-GB' : 'it-IT') as Intl.Locale & { getWeekInfo?: () => { firstDay: number }; weekInfo?: { firstDay: number } };
    return loc.getWeekInfo?.().firstDay ?? loc.weekInfo?.firstDay ?? 1;
  } catch {
    return 1;
  }
}

function setNativeValue(input: HTMLInputElement, value: string) {
  const setter = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value')?.set;
  setter?.call(input, value);
  input.dispatchEvent(new Event('input', { bubbles: true }));
  input.dispatchEvent(new Event('change', { bubbles: true }));
}

export const DateInput = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(function DateInput({ className, ...props }, ref) {
  const inner = useRef<HTMLInputElement | null>(null);
  const wrap = useRef<HTMLDivElement | null>(null);
  const pop = useRef<HTMLDivElement | null>(null);
  const [open, setOpen] = useState(false);
  const [up, setUp] = useState(false);
  const [view, setView] = useState<{ y: number; m: number }>(() => {
    const d = new Date();
    return { y: d.getFullYear(), m: d.getMonth() };
  });
  const lang = pageLang();
  const L = LABELS[lang];
  const locale = lang === 'en' ? 'en-GB' : 'it-IT';

  const setRefs = useCallback(
    (el: HTMLInputElement | null) => {
      inner.current = el;
      if (typeof ref === 'function') ref(el);
      else if (ref) ref.current = el;
    },
    [ref],
  );

  const current = () => inner.current?.value ?? '';

  const show = () => {
    if (props.disabled || props.readOnly) return;
    const v = current();
    const base = /^\d{4}-\d{2}-\d{2}$/.test(v) ? v : todayIso();
    setView({ y: Number(base.slice(0, 4)), m: Number(base.slice(5, 7)) - 1 });
    setOpen(true);
  };

  // under the field, or above it when the dialog (or the window) has no room below; absolute, not fixed: the
  // dialog is transformed and scrolls, a fixed popover would land in the wrong place or be clipped
  useLayoutEffect(() => {
    if (!open || !wrap.current) return;
    const r = wrap.current.getBoundingClientRect();
    const box = wrap.current.closest('.dialog')?.getBoundingClientRect() ?? { top: 0, bottom: window.innerHeight };
    const h = pop.current?.offsetHeight ?? 320;
    const below = box.bottom - r.bottom;
    const above = r.top - box.top;
    setUp(below < h + 8 && above > below);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const down = (e: PointerEvent) => {
      const t = e.target as Node;
      if (!wrap.current?.contains(t) && !pop.current?.contains(t)) setOpen(false);
    };
    const key = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        // only the calendar closes, not the dialog around it
        e.stopPropagation();
        setOpen(false);
        inner.current?.focus();
      }
    };
    // window, capture: before the dialog's own Escape listener on the document, so only the calendar closes
    document.addEventListener('pointerdown', down, true);
    window.addEventListener('keydown', key, true);
    return () => {
      document.removeEventListener('pointerdown', down, true);
      window.removeEventListener('keydown', key, true);
    };
  }, [open]);

  const pick = (value: string) => {
    if (inner.current) setNativeValue(inner.current, value);
    setOpen(false);
    inner.current?.focus();
  };

  const fd = firstDay(lang);
  const first = new Date(view.y, view.m, 1);
  const lead = (first.getDay() - (fd % 7) + 7) % 7;
  const days = new Date(view.y, view.m + 1, 0).getDate();
  const cells: (number | null)[] = [...Array<null>(lead).fill(null), ...Array.from({ length: days }, (_, i) => i + 1)];
  while (cells.length % 7) cells.push(null);
  const weekdays = Array.from({ length: 7 }, (_, i) => new Intl.DateTimeFormat(locale, { weekday: 'short' }).format(new Date(2024, 0, 1 + ((fd - 1 + i) % 7))));
  const title = new Intl.DateTimeFormat(locale, { month: 'long', year: 'numeric' }).format(first);
  const selected = current();
  const today = todayIso();
  const min = typeof props.min === 'string' ? props.min : undefined;
  const max = typeof props.max === 'string' ? props.max : undefined;
  const move = (n: number) => setView(({ y, m }) => ({ y: m + n < 0 ? y - 1 : m + n > 11 ? y + 1 : y, m: (m + n + 12) % 12 }));

  return (
    <div className="date-input" ref={wrap}>
      <input
        ref={setRefs}
        type="date"
        className={['input', className ?? ''].filter(Boolean).join(' ')}
        {...props}
        onKeyDown={(e) => {
          props.onKeyDown?.(e);
          if ((e.key === 'ArrowDown' && e.altKey) || e.key === 'F4') {
            e.preventDefault();
            show();
          }
        }}
      />
      <button type="button" className="date-input-btn" aria-label={L.open} aria-expanded={open} tabIndex={-1} disabled={props.disabled} onClick={() => (open ? setOpen(false) : show())}>
        <CalendarDays size={18} aria-hidden />
      </button>
      {open && (
        <div ref={pop} className={up ? 'date-pop up' : 'date-pop'} role="dialog" aria-label={title}>
          <div className="date-pop-head">
            <button type="button" className="btn quiet icon sm" aria-label={L.prev} onClick={() => move(-1)}>
              <ChevronLeft size={18} aria-hidden />
            </button>
            <span className="date-pop-title">{title}</span>
            <button type="button" className="btn quiet icon sm" aria-label={L.next} onClick={() => move(1)}>
              <ChevronRight size={18} aria-hidden />
            </button>
          </div>
          <div className="date-grid" role="grid">
            {weekdays.map((w) => (
              <span key={w} className="date-wd" role="columnheader">
                {w}
              </span>
            ))}
            {cells.map((d, i) => {
              if (d === null) return <span key={`e${i}`} />;
              const v = iso(view.y, view.m, d);
              const off = (min && v < min) || (max && v > max);
              return (
                <button
                  key={v}
                  type="button"
                  role="gridcell"
                  className="date-day"
                  aria-selected={v === selected}
                  aria-current={v === today ? 'date' : undefined}
                  disabled={!!off}
                  onClick={() => pick(v)}
                >
                  {d}
                </button>
              );
            })}
          </div>
          <div className="date-pop-foot">
            {!props.required ? (
              <button type="button" className="btn quiet sm" onClick={() => pick('')}>
                {L.clear}
              </button>
            ) : (
              <span />
            )}
            <button type="button" className="btn quiet sm" disabled={!!((min && today < min) || (max && today > max))} onClick={() => pick(today)}>
              {L.today}
            </button>
          </div>
        </div>
      )}
    </div>
  );
});
